import React, { useContext, useCallback } from 'react';
import styled from 'styled-components';
import { SelectField, FlexColumn, Toggle } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import CategoryDisplay from '../shared/CategoryDisplay';
import AssigneeDisplay from '../shared/AssigneeDisplay';
import PriorityDisplay from '../shared/PriorityDisplay';
import CreateEmployeeTicketShiftPicker from './CreateEmployeeTicketShiftPicker';
import { FieldText } from './createTicketStyles';
import useTicketClassificationFields from './hooks/useTicketClassificationFields';

const Container = styled(FlexColumn)`
  gap: 16px;
  padding: 24px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const ShiftPickerWrap = styled(FlexColumn)`
  flex: 1;
  min-height: 0;
  background: ${({ theme }) => theme.colors.grey1};
  border-radius: 8px;
  padding: 8px;
  overflow: hidden;
`;

const Step2Classification = () => {
  const { t } = useContext(ConfigContext);
  const {
    branchId,
    employeeId,
    categoriesLoading,
    categoriesById,
    adminsById,
    categoryOptions,
    adminOptions,
    priorityOptions,
    shiftRelated,
    handleShiftToggle,
    handleCategoryChange
  } = useTicketClassificationFields();

  const formatCategoryOption = useCallback(
    (option) => ({
      ...option,
      label: (
        <FieldText>
          <CategoryDisplay category={option.category} />
        </FieldText>
      )
    }),
    []
  );
  const formatCategoryValue = useCallback(
    ({ value }) => {
      const category = categoriesById[value];
      return category ? (
        <FieldText>
          <CategoryDisplay category={category} />
        </FieldText>
      ) : null;
    },
    [categoriesById]
  );

  const formatAdminOption = useCallback(
    (option) => ({
      ...option,
      label: <FieldText>{option.admin ? <AssigneeDisplay admin={option.admin} /> : option.label}</FieldText>
    }),
    []
  );
  const formatAdminValue = useCallback(
    ({ value }) => {
      if (value == null) return null;
      const admin = adminsById[value];
      return admin ? (
        <FieldText>
          <AssigneeDisplay admin={admin} />
        </FieldText>
      ) : null;
    },
    [adminsById]
  );

  const formatPriorityOption = useCallback(
    (option) => ({
      ...option,
      label: (
        <FieldText>
          <PriorityDisplay priority={option.priority} />
        </FieldText>
      )
    }),
    []
  );
  const formatPriorityValue = useCallback(
    ({ value }) =>
      value ? (
        <FieldText>
          <PriorityDisplay priority={value} />
        </FieldText>
      ) : null,
    []
  );

  return (
    <Container>
      <SelectField
        name="categoryId"
        title={t('help_desk_category')}
        options={categoryOptions}
        formatOption={formatCategoryOption}
        formatValue={formatCategoryValue}
        isLoading={categoriesLoading}
        isMulti={false}
        usePortal
        required
        disabled={!branchId}
        onChange={handleCategoryChange}
      />
      <SelectField
        name="assignedAdminId"
        title={t('help_desk_assign_to_admin')}
        options={adminOptions}
        formatOption={formatAdminOption}
        formatValue={formatAdminValue}
        isMulti={false}
        usePortal
        disabled={!branchId}
      />
      <SelectField
        name="priority"
        title={t('help_desk_priority')}
        options={priorityOptions}
        formatOption={formatPriorityOption}
        formatValue={formatPriorityValue}
        isMulti={false}
        usePortal
      />
      <Toggle value={shiftRelated} onChange={handleShiftToggle} text={t('help_desk_ticket_related_to_shift')} />
      {shiftRelated && (
        <ShiftPickerWrap>
          <CreateEmployeeTicketShiftPicker employeeId={employeeId} branchId={branchId} />
        </ShiftPickerWrap>
      )}
    </Container>
  );
};

export default Step2Classification;
